/**
 * Lightweight client feature flags with a dev-console override layer, mirroring
 * the artemas pattern. Toggle any flag from the browser console on non-production
 * builds (a reload is required — flags are read at render time):
 *
 *   featureFlags.on('subscribe-buttons');   location.reload();
 *   featureFlags.off('subscribe-buttons');  location.reload();
 *   featureFlags.clear();                    location.reload();   // all
 *   featureFlags.list();
 *
 * Overrides are ignored on production builds so a flag can never leak via a
 * shared machine; production behaviour is whatever `DEFAULTS` says (to change
 * it for live users, flip the default here and redeploy).
 */

const STORAGE_KEY = 'geo.ff.overrides';

/**
 * Flag defaults.
 *
 * `subscribe-buttons` OFF (the current state, awaiting the tax ID) hides the
 * subscribe / checkout / pay buttons — they stay in the DOM — and shows a
 * "Contact us" mailto link in their place. Flip to `true` here (or
 * `featureFlags.on('subscribe-buttons')` on non-prod) to restore the originals.
 */
const DEFAULTS: Record<string, boolean> = {
  'subscribe-buttons': false,
};

/** Overrides apply on local/dev builds, never on a production build. */
const overridesEnabled = !import.meta.env.PROD;

type Overrides = Record<string, boolean>;

function read(): Overrides {
  if (!overridesEnabled) return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Overrides)
      : {};
  } catch {
    return {};
  }
}

function write(next: Overrides): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // private mode / quota — overrides are a dev convenience, ignore.
  }
}

/** Returns the effective value for `flag`: dev override → default → false. */
export function isFeatureEnabled(flag: string): boolean {
  const override = read()[flag];
  if (override !== undefined) return override;
  return DEFAULTS[flag] ?? false;
}

/** Installs `window.featureFlags` so flags can be toggled from the console. */
export function installFeatureFlagConsole(): void {
  if (!overridesEnabled) return;

  const api = {
    on(flag: string): string {
      write({ ...read(), [flag]: true });
      return `Override set: ${flag}=true. Reload to apply.`;
    },
    off(flag: string): string {
      write({ ...read(), [flag]: false });
      return `Override set: ${flag}=false. Reload to apply.`;
    },
    clear(flag?: string): string {
      if (flag === undefined) {
        write({});
        return "All flag overrides cleared. Reload to apply.";
      }
      const next = read();
      delete next[flag];
      write(next);
      return `Override cleared: ${flag}. Reload to apply.`;
    },
    list(): Overrides {
      return read();
    },
  };

  (globalThis as unknown as { featureFlags?: typeof api }).featureFlags = api;
}

installFeatureFlagConsole();
